import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as CloseIcon } from '@assets/icons/close.svg';

import CustomDropdown from '@components/CustomDropdown';
import { toastError, toastSuccess } from '@components/Toast';

import BaseSizeAndQtyModal from '@features/costing/components/BaseSizeAndQtyModal';
import CostingAccordion from '@features/costing/components/CostingAccordion';
import CostingFields from '@features/costing/components/CostingFields';
import { generateCostingListParams } from '@features/filter/static/filterUtil';

import { fetchCostingDetails, setUpdateProductItemInfo } from '@store/actions/CostingAction';
import { fetchProductInfo } from '@store/actions/DesignToolAction';
import { handleFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { updateLeadTime } from '@services/commonService';
import {
	findAllSupplier,
	onUploadInitialCostingDocuments,
	removeInitialCostingDocument,
	setFactoryCosting
} from '@services/costingService';
import { completeReview } from '@services/quoteServices';

import ApiClient from '@lib/apiClient';

import {
	changeDateFormat,
	checkAnyUserType,
	checkUserType,
	convertDateToLocal,
	isValidInput,
	onMultipleFileUpload,
	printApiError,
	STATIC_SET_CONSTANTS,
	validateFloatNumber,
	validateNumber
} from '@utils/Util';

const COSTING_DROPDOWN_OPTIONS = [
	{
		value: 'IN_RANGE',
		label: 'In range',
		className: 'status green-1'
	},
	{
		value: 'OUT_OF_RANGE',
		label: 'Out of range',
		className: 'status red-2'
	},
	{
		value: 'IN_CONSIDERATION',
		label: 'In consideration',
		className: 'status yellow-1'
	},
	{
		value: 'RECHECK',
		label: 'Recheck',
		className: 'status blue-1'
	},
	{
		value: 'TARGET_PRICE_MISSING',
		label: 'Target price missing',
		className: 'status red-3'
	}
];

const ALLOW_EDIT = ['COSTING_MANAGER', 'ACCOUNT_MANAGER', 'ADMIN'];
const CostingForm = ({
	costingId,
	productId,
	onUpdateCostingStatus,
	isCostingRequestFromBuyer,
	isCostingForSetItem
}) => {
	const [showMore, setShowMore] = useState(true);
	const [viewSize, setViewSize] = useState(false);
	const [readOnly, setReadOnly] = useState(true);
	const [qtyError, setQtyError] = useState(false);
	const [sizeError, setSizeError] = useState(false);
	const [inputData, setInputData] = useState({});
	const [leadTime, setLeadTime] = useState('');
	const [increaseCostField, setIncreaseCostField] = useState(false);
	const [costList, setCostList] = useState([]);
	const dispatch = useDispatch();
	const isCostingManager = () => checkUserType('COSTING_MANAGER');
	const prevTarget = useRef(null);
	const [isShowFactoryCosting, setIsShowFactoryCosting] = useState(false);
	const [isCompleteBtnDisabled, setIsCompleteBtnDisabled] = useState(false);
	const [inputValueOfSetMembers, setInputValueOfSetMembers] = useState([]);
	const [costingDetailsForSet, setCostingDetailsForSet] = useState({});
	const [selectedSupplier, setSelectedSupplier] = useState({});
	const [suppliers, setSuppliers] = useState([]);
	const [documents, setDocuments] = useState([]);
	const activeTabInfo = useSelector((store) => store.costingStore.activeTabInfo);
	const filterfields = useSelector((store) => store.filterFieldStore);
	const sortValue = filterfields['sort'] && filterfields['sort'][0]?.key;
	const [selectedPriceRangeStatus, setSelectedPriceRangeStatus] = useState({
		value: 'IN_RANGE',
		label: 'In range',
		className: 'status green-1'
	});

	const aiGeneratedCostRoles = () => {
		return checkAnyUserType(['ADMIN', 'PRODUCT_DEVELOPMENT', 'COSTING_MANAGER']);
	};

	const handleDropdownChange = async (selectedPriceRangeStatus) => {
		setSelectedPriceRangeStatus(selectedPriceRangeStatus);
		try {
			dispatch(openLoader());
			const postData = {
				...inputData,
				costingReviewAssessment: selectedPriceRangeStatus?.value,
				productId: productId
			};
			await ApiClient.POST('addInitialCosting', postData);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const getSortingValue = () => {
		if (sortValue === 'id,desc') return 'initialCosting.priceLastModifiedAt,desc';
		else {
			return 'initialCosting.priceLastModifiedAt,asc';
		}
	};

	const getSupplierList = async (params) => {
		try {
			dispatch(openLoader());
			const response = await findAllSupplier(params);
			setSuppliers(response?.data?.map((item) => ({ id: item.id, value: item.name })));
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (checkAnyUserType(ALLOW_EDIT)) {
			setReadOnly(false);
		}

		(async () => {
			await getSupplierList();
		})();
	}, []);

	const generateCostingColumn = (cloneData) => {
		let newArray = [];
		for (let i = 0; i < Math.max(cloneData.length, 2) + 1; i++) {
			newArray.push({
				id: cloneData[i]?.id ? cloneData[i].id : null,
				minQuantity: cloneData[i]?.minQuantity ? cloneData[i].minQuantity : null,
				price: cloneData[i]?.price ? cloneData[i].price : null,
				productId: costingId
			});
		}
		return newArray;
	};

	const getApi = () =>
		isCostingRequestFromBuyer ? 'fetchQuantityWiseCostFromCosting' : 'fetchQuantityWiseCost';

	const getInitialCostingApi = () =>
		isCostingRequestFromBuyer ? 'fetchInitialCostingFromCosting' : 'fetchInitialCosting';

	const fetchQuantityWiseCost = async () => {
		await ApiClient.GET(getApi(), costingId)
			.then(({ data }) => {
				let cloneData = [...data.quantityWiseInitialCostingResponseList];
				for (let item of cloneData) {
					item.price = item.price ? Number(item.price).toFixed(2) : null;
				}
				setCostList(generateCostingColumn(cloneData));
			})
			.catch((error) => {
				toastError(error.response.data.message);
			});
	};

	const format = (number) => {
		if (!number || isNaN(number)) return null;
		return Number(number).toFixed(2);
	};

	const formatAllPrice = (tmpInputData) => {
		let tmpArray = [
			'fabricUnitCost',
			'trimsCost',
			'embellishmentCost',
			'washCost',
			'cmCost',
			'accessoriesCost',
			'testingCost',
			'commercialCost',
			'allowance'
		];
		for (let item of tmpArray) {
			tmpInputData[item] = format(tmpInputData[item]);
		}
		if (tmpInputData?.totalCost) {
			tmpInputData['basePrice'] = tmpInputData['totalCost'].toFixed(2);
		}
		return tmpInputData;
	};

	const getFormattedSetMembersInput = (initialCostResponse = []) => {
		return initialCostResponse.map((member) => {
			let tmpInputData = formatAllPrice(member);
			return {
				...member,
				...tmpInputData
			};
		});
	};

	const fetchInitialCosting = async () => {
		try {
			const response = await ApiClient.GET(getInitialCostingApi(), costingId);
			setLeadTime(response.data?.leadTime || '');
			setDocuments(response.data.documentResponseList);
			setIsShowFactoryCosting(response.data.isFactoryCost || false);
			setSelectedPriceRangeStatus(
				COSTING_DROPDOWN_OPTIONS.find(
					(option) => option?.value === response?.data?.costingReviewAssessment?.constant
				)
			);
			if (response.data?.supplier?.id) {
				setSelectedSupplier({ id: response.data.supplier.id, value: response.data.supplier.name });
			}
			if (response.data.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT) {
				setInputValueOfSetMembers(
					getFormattedSetMembersInput(response?.data?.memberInitialCostingResponseList)
				);
				setCostingDetailsForSet({
					id: response?.data?.id,
					moq: response?.data?.moq,
					baseSize: response?.data?.baseSize,
					totalCost: response?.data?.totalCost,
					priceLastModifiedAt: response?.data?.priceLastModifiedAt,
					priceLastModifiedBy: response?.data?.priceLastModifiedBy,
					isQuoted: response?.data?.isQuoted,
					isSet: response?.data?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT
				});
			} else {
				let tmpInputData = formatAllPrice(response.data);
				setInputData({
					...response.data,
					...tmpInputData,
					costingReviewAssessment: tmpInputData?.costingReviewAssessment?.constant
				});
			}
		} catch (e) {
			printApiError(e);
		}
	};

	useEffect(() => {
		try {
			dispatch(openLoader());
			Promise.all([fetchInitialCosting(), fetchQuantityWiseCost()]).finally(() =>
				dispatch(closeLoader())
			);
		} catch {
			dispatch(closeLoader());
		}
	}, [costingId]);

	const handleRemarks = (e) => {
		let cloneInput = { ...inputData };
		cloneInput['remarks'] = e.target.value;
		setInputData(cloneInput);
	};

	const handleLeadTime = (e) => {
		setLeadTime(e.target.value);
	};

	const resetAll = () => {
		prevTarget.current = null;
	};

	const saveRemarks = () => {
		dispatch(openLoader());
		let postData = {
			remarks: inputData?.remarks
		};
		if (isCostingRequestFromBuyer) {
			postData['initialCostingId'] = costingId;
		} else {
			postData['productId'] = costingId;
		}
		ApiClient.POST('updateCostingRemarks', postData)
			.then((response) => {
				resetAll();
				toastSuccess(response.data.message);
				dispatch(
					setUpdateProductItemInfo({
						productId: costingId,
						totalCost: response.data.payload.totalCost,
						remarks: response.data.payload.remarks
					})
				);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};

	const handleUpdate = (isBaseSizeUpdate = false) => {
		if (!inputData.moq || !costingDetailsForSet.moq) {
			setQtyError(true);
		}
		if (!inputData.baseSize || !costingDetailsForSet.baseSize) {
			setSizeError(true);
		}
		if (
			(inputData.moq && inputData.baseSize) ||
			(costingDetailsForSet.moq && costingDetailsForSet.baseSize)
		) {
			setSizeError(false);
			setQtyError(false);
			updateCosting('', false, true, undefined, false, isBaseSizeUpdate);
			setViewSize(false);
		}
	};

	const submitProcessFromAllCost = (memberId) => {
		if (readOnly) return;
		const postDataOfSetMembers = inputValueOfSetMembers.find((item) => item.id === memberId);
		if (Object?.keys(postDataOfSetMembers?.costingReviewAssessment)?.length === 0) {
			delete postDataOfSetMembers?.costingReviewAssessment;
		}
		dispatch(openLoader());
		if (isCostingRequestFromBuyer) {
			postDataOfSetMembers['initialCostingId'] = postDataOfSetMembers.id;
		} else {
			postDataOfSetMembers['productId'] = costingId;
		}

		ApiClient.POST('processFromAllCost', postDataOfSetMembers)
			.then((response) => {
				toastSuccess(response.data.message);
				resetAll();
				if (onUpdateCostingStatus) {
					onUpdateCostingStatus();
				}
				setCostingDetailsForSet(response?.data?.payload);
				// dispatch(fetchProductInfo(productId));
				fetchQuantityWiseCost();
				setInputValueOfSetMembers(
					getFormattedSetMembersInput(response?.data.payload?.memberInitialCostingResponseList)
				);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};

	const fetchCostInfoFromCostField = () => {
		if (readOnly) return;
		let postData = { ...inputData };
		dispatch(openLoader());
		if (isCostingRequestFromBuyer) {
			postData['initialCostingId'] = costingId;
		} else {
			delete postData['id'];
			postData['productId'] = costingId;
		}
		ApiClient.POST('processFromAllCost', postData)
			.then((response) => {
				resetAll();
				setInputData({
					...response.data.payload,
					costingReviewAssessment: response.data.payload?.costingReviewAssessment?.constant
				});
				toastSuccess(response.data.message);
				if (isCostingRequestFromBuyer) {
					fetchQuantityWiseCost();
				}
				if (onUpdateCostingStatus) {
					onUpdateCostingStatus();
				} else {
					dispatch(fetchCostingDetails(costingId));
				}
				dispatch(
					setUpdateProductItemInfo({
						productId: productId,
						isQuoted: response.data.payload.isQuoted,
						priceLastModifiedAt: response.data.payload.priceLastModifiedAt,
						totalCost: response.data.payload.totalCost
					})
				);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};

	// update costing for set members
	const updateSetMembersCosting = (e, memberId) => {
		const postDataOfSetMembers = inputValueOfSetMembers.find((item) => item.id === memberId);
		dispatch(openLoader());
		if (isCostingRequestFromBuyer) {
			postDataOfSetMembers['initialCostingId'] = postDataOfSetMembers.id;
		} else {
			postDataOfSetMembers['productId'] = costingId;
		}
		delete postDataOfSetMembers['basePrice'];
		ApiClient.POST('addInitialCosting', postDataOfSetMembers)
			.then((response) => {
				toastSuccess(response.data.message);
				resetAll();
				if (onUpdateCostingStatus) {
					onUpdateCostingStatus();
				}
				setCostingDetailsForSet(response?.data?.payload);
				// dispatch(fetchProductInfo(productId));
				fetchQuantityWiseCost();
				setInputValueOfSetMembers(
					getFormattedSetMembersInput(response?.data.payload?.memberInitialCostingResponseList)
				);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error?.response?.data?.message);
				dispatch(closeLoader());
			});
	};

	// update base size and qty price
	const updateBasePriceAndQty = () => {
		let basePriceData = {
			totalCost: costingDetailsForSet?.totalCost,
			baseSize: costingDetailsForSet?.baseSize,
			id: costingDetailsForSet?.id,
			initialCostingId: costingDetailsForSet?.initialCostingId,
			moq: costingDetailsForSet?.moq
		};

		dispatch(openLoader());
		if (isCostingRequestFromBuyer) {
			basePriceData['initialCostingId'] = costingId;
		} else {
			basePriceData['productId'] = costingId;
		}
		ApiClient.POST('addInitialCosting', basePriceData)
			.then((response) => {
				toastSuccess(response.data.message);
				resetAll();
				if (onUpdateCostingStatus) {
					onUpdateCostingStatus();
				}
				fetchQuantityWiseCost();
				setCostingDetailsForSet(response?.data?.payload);
				setInputValueOfSetMembers(
					getFormattedSetMembersInput(response?.data.payload?.memberInitialCostingResponseList)
				);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};
	// validate base qty size and price input and than update.
	const handleBaseSizeAndQtyForSetUpdate = () => {
		if (!costingDetailsForSet.moq) {
			setQtyError(true);
		}
		if (!costingDetailsForSet.baseSize) {
			setSizeError(true);
		}
		if (costingDetailsForSet.moq && costingDetailsForSet.baseSize) {
			setSizeError(false);
			setQtyError(false);
			updateBasePriceAndQty();
			setViewSize(false);
		}
	};

	const handleClose = () => {
		setViewSize(false);
	};

	const updateCosting = async (
		e,
		// eslint-disable-next-line no-unused-vars
		isRemoveBasePrice = true,
		isShowToast = false,
		supplierId,
		removeTotalCost = true,
		isBaseSizeUpdate = false
	) => {
		//  factory costing if applicable
		let responseFromFactoryCosting;
		if (isShowFactoryCosting && isBaseSizeUpdate) {
			responseFromFactoryCosting = await toggleFactoryCosting(isShowFactoryCosting);
		}

		let postData = responseFromFactoryCosting
			? {
					...responseFromFactoryCosting?.payload,
					moq: inputData?.moq,
					totalCost: inputData?.totalCost,
					baseSize: inputData?.baseSize,
					costingReviewAssessment: selectedPriceRangeStatus?.value
				}
			: {
					...inputData,
					costingReviewAssessment: selectedPriceRangeStatus?.value
				};

		delete postData['basePrice'];
		if (removeTotalCost) delete postData['totalCost'];
		dispatch(openLoader());
		if (isCostingRequestFromBuyer) {
			postData['initialCostingId'] = costingId;
		} else {
			postData['productId'] = costingId;
		}
		if (supplierId) {
			postData['supplierId'] = supplierId;
		}

		ApiClient.POST('addInitialCosting', postData)
			.then((response) => {
				if (isShowToast) {
					toastSuccess(response.data.message);
				}
				resetAll();
				if (onUpdateCostingStatus) {
					onUpdateCostingStatus();
				}
				dispatch(fetchProductInfo(productId));
				fetchQuantityWiseCost();
				setInputData({
					...formatAllPrice(response.data.payload),
					costingReviewAssessment: response.data.payload?.costingReviewAssessment?.constant
				});
				dispatch(
					setUpdateProductItemInfo({
						productId: productId,
						isQuoted: response.data.payload.isQuoted,
						priceLastModifiedAt: response.data.payload.priceLastModifiedAt,
						totalCost: response.data.payload.totalCost,
						remarks: response.data.payload.remarks
					})
				);
				dispatch(closeLoader());
			})
			.catch((error) => {
				toastError(error.response.data.message);
				dispatch(closeLoader());
			});
	};
	// handle set items base costing.
	const handleBaseCostingChange = (e) => {
		if (e.target.value && parseFloat(e.target.value) < 0) {
			e.preventDefault();
			return;
		}
		let baseCostingData = { ...costingDetailsForSet };
		let value = e.target.value;
		baseCostingData[e.target.name] = value ? value : null;
		setCostingDetailsForSet(baseCostingData);
	};

	// handle input Change for regular product such as pack or single design.
	const handleChange = (e) => {
		if (e.target.value && parseFloat(e.target.value) < 0) {
			e.preventDefault();
			return;
		}
		let postData = { ...inputData };
		let value = e.target.value;
		postData[e.target.name] = value ? value : null;
		setInputData(postData);
	};
	// handle input change for set members
	const handleSetMemberInputChange = (e, memberId) => {
		if (e.target.value && parseFloat(e.target.value) < 0) {
			e.preventDefault();
			return;
		}
		const updatedSetMembersValue = inputValueOfSetMembers.map((member) => {
			if (memberId === member?.id) {
				let value = e.target.value;
				return {
					...member,
					[e.target.name]: value ? value : null
				};
			}
			return member;
		});
		setInputValueOfSetMembers(updatedSetMembersValue);
	};

	const handleCostingChange = (e, index) => {
		if (e.target.value && parseFloat(e.target.value) < 0) {
			e.preventDefault();
			return;
		}
		let costingList = [...costList];
		for (let i = 0; i < costingList.length; i++) {
			if (i === index) {
				costingList[i][e.target.name] = e.target.value ? e.target.value : null;
			}
		}
		setCostList(costingList);
	};

	const saveCosting = (e, i) => {
		// if (readOnly) return;
		let { name } = e.target;
		let selectedField = costList?.find((item, index) => index === i);
		let duplicateQty = costList?.filter(
			(item) => parseFloat(item.minQuantity) === parseFloat(selectedField.minQuantity)
		);
		if (duplicateQty.length > 1) {
			toastError('This quantity already exist.');
		} else if (selectedField?.id || (selectedField?.minQuantity && selectedField?.price)) {
			dispatch(openLoader());
			if (isCostingRequestFromBuyer) {
				delete selectedField['productId'];
				selectedField['initialCostingId'] = costingId;
			}
			ApiClient.POST('saveQuantityWiseCost', selectedField)
				.then(({ data }) => {
					toastSuccess(data.message);
					resetAll();
					let cloneData = [...data.payload.quantityWiseInitialCostingResponseList];
					if (name === 'price') {
						for (let item of cloneData) {
							let value = item.price ? Number(item.price).toFixed(2) : null;
							item.price = value;
						}
					}
					setCostList(generateCostingColumn(cloneData));
					onUpdateCostingStatus();
					dispatch(closeLoader());
				})
				.catch((error) => {
					toastError(error.response.data.message);
					dispatch(closeLoader());
				});
		}
	};

	const renderCostList = () => {
		return costList?.map((item, index) => {
			return (
				<tr key={`costList_${index}`}>
					<td>
						<input
							type="number"
							name="minQuantity"
							min={0}
							value={item?.minQuantity || ''}
							disabled={(index === 0 && isCostingRequestFromBuyer) || readOnly}
							onKeyPress={validateNumber}
							onChange={(e) => handleCostingChange(e, index)}
							onFocus={(event) => (prevTarget.current = event.target.value)}
							onBlur={(event) => {
								if (!isValidInput(prevTarget, event)) {
									resetAll();
									return;
								}
								saveCosting(event, index);
							}}
						/>
					</td>
					<td>
						<input
							type="number"
							name="price"
							min={0}
							value={item?.price || ''}
							disabled={(index === 0 && isCostingRequestFromBuyer) || readOnly}
							onKeyPress={validateFloatNumber}
							onChange={(e) => handleCostingChange(e, index)}
							onFocus={(event) => (prevTarget.current = event.target.value)}
							onBlur={(event) => {
								if (!isValidInput(prevTarget, event)) {
									resetAll();
									return;
								}
								saveCosting(event, index);
							}}
						/>
					</td>
				</tr>
			);
		});
	};

	const saveLeadTime = async (e) => {
		try {
			dispatch(openLoader());
			let postData = { leadTime: e.target.value };
			if (isCostingRequestFromBuyer) {
				postData['initialCostingId'] = costingId;
			}
			postData['productId'] = productId;
			await updateLeadTime(postData);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const formatTotalCost = (totalCost) => {
		return isNaN(Number(totalCost)) ? '' : Number(totalCost).toFixed(2);
	};

	const renderBaseQtyAndCostingDetails = (qtyAndPriceDetails) => {
		return (
			<span className="gray_dark_02">
				{qtyAndPriceDetails?.totalCost
					? `$${formatTotalCost(qtyAndPriceDetails.totalCost)}/`
					: 'Price/'}
				{qtyAndPriceDetails?.baseSize ? qtyAndPriceDetails.baseSize : 'Size'}/
				{qtyAndPriceDetails?.moq ? qtyAndPriceDetails.moq : 'Qty'}
			</span>
		);
	};

	const renderTotalCost = (totalCost = '0.00') => {
		return <span className="regular-16 gray_dark_01 weight-500">${formatTotalCost(totalCost)}</span>;
	};

	const renderBaseSizeAndCostingModal = () => {
		return (
			<>
				{viewSize && !isCostingForSetItem && (
					<BaseSizeAndQtyModal
						handleChange={handleChange}
						handleClose={handleClose}
						inputData={inputData}
						qtyError={qtyError}
						handleUpdate={handleUpdate}
						validateNumber={validateNumber}
						validateFloatNumber={validateFloatNumber}
						sizeError={sizeError}
						isPriceDisabled={!isShowFactoryCosting}
					/>
				)}

				{viewSize && isCostingForSetItem && (
					// for set base qty and size
					<BaseSizeAndQtyModal
						handleChange={handleBaseCostingChange}
						handleClose={handleClose}
						inputData={costingDetailsForSet}
						qtyError={qtyError}
						handleUpdate={handleBaseSizeAndQtyForSetUpdate}
						validateNumber={validateNumber}
						validateFloatNumber={validateFloatNumber}
						sizeError={sizeError}
						isPriceDisabled={!isShowFactoryCosting}
					/>
				)}
			</>
		);
	};

	const renderSizeQtyBtn = () => {
		return (
			<button
				className="button text regular-14 d-flex"
				disabled={readOnly}
				onClick={() => setViewSize(true)}
			>
				{isCostingForSetItem
					? renderBaseQtyAndCostingDetails(costingDetailsForSet)
					: renderBaseQtyAndCostingDetails(inputData)}

				<img
					src={`${process.env.PUBLIC_URL}/icons/arrow_drop_up.svg`}
					alt="down arrow"
					className="down"
				/>
			</button>
		);
	};

	const onUploadDocument = async (documents) => {
		try {
			dispatch(openLoader());
			await onUploadInitialCostingDocuments(inputData.id, documents);
			await fetchInitialCosting();
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const removeDocumentById = async (documentId) => {
		try {
			dispatch(openLoader());
			await removeInitialCostingDocument(inputData.id, documentId);
			await fetchInitialCosting();
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const handleDownloadFile = async (document) => {
		try {
			dispatch(openLoader());

			const response = await fetch(document.docUrl);
			const blob = await response.blob();

			const url = window.URL.createObjectURL(blob);
			const link = window.document.createElement('a');
			link.href = url;
			link.setAttribute('download', document.name);
			window.document.body.appendChild(link);
			link.click();
			link.parentNode.removeChild(link);
			window.URL.revokeObjectURL(url);

			dispatch(closeLoader());
		} catch (error) {
			printApiError(error);
			dispatch(closeLoader());
		}
	};

	const toggleFactoryCosting = async (isFactoryCosting) => {
		try {
			dispatch(openLoader());
			const params = `?isFactoryCosting=${isFactoryCosting}&${
				isCostingRequestFromBuyer ? `initialCostingId=${costingId}` : `productId=${productId}`
			}`;
			const response = await setFactoryCosting(params);
			toastSuccess(response?.message);
			dispatch(closeLoader());
			return response;
		} catch {
			dispatch(closeLoader());
		}
	};

	const renderDocumentList = () => {
		return documents?.map((item) => (
			<span className="tag" key={`document_${item.id}`}>
				<span className="text-underline cursor-pointer" onClick={() => handleDownloadFile(item)}>
					{item.name}
				</span>
				<span onClick={() => removeDocumentById(item.id)}>
					<CloseIcon />
				</span>
			</span>
		));
	};

	const onCompleteReview = async () => {
		try {
			dispatch(openLoader());
			const response = await completeReview([productId]);
			let requestBody = generateCostingListParams(filterfields, activeTabInfo.filterStatus);
			let queryString = `?page=0&size=20&sort=${getSortingValue()}`;
			await dispatch(handleFilter(activeTabInfo.listApiUrl, queryString, false, 'POST', requestBody));
			setIsCompleteBtnDisabled(true);
			toastSuccess(response?.message);
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handlePredictCost = async (costingType) => {
		if (costingType === 'AI') {
			window.open(`/designs/edit/${productId}?activeTab=costing&isAiCosting=true`, '_self');
		} else {
			window.open(`/designs/edit/${productId}?activeTab=costing`, '_self');
		}
	};

	return (
		<div className="design-tool-sidebar">
			<div className="sidebar-costing-container scroll-y-label">
				{activeTabInfo?.activeTabName === 'DESIGN_STUDIO_IN_REVIEW' && (
					<button
						className="button text-only blue review-button"
						onClick={() => onCompleteReview()}
						disabled={isCompleteBtnDisabled}
					>
						Complete Review
					</button>
				)}

				<div className="costing-data">
					<div disabled={isShowFactoryCosting}>
						{isCostingForSetItem ? (
							<CostingAccordion
								showMore={showMore}
								setShowMore={setShowMore}
								submitProcessFromAllCost={submitProcessFromAllCost}
								onSetMemberInputChange={handleSetMemberInputChange}
								increaseCostField={increaseCostField}
								inputValueOfSet={inputValueOfSetMembers}
								prevTarget={prevTarget}
								readOnly={readOnly}
								resetAll={resetAll}
								setIncreaseCostField={setIncreaseCostField}
								updateSetMembersCosting={updateSetMembersCosting}
							/>
						) : (
							<CostingFields
								fetchCostInfoFromCostField={fetchCostInfoFromCostField}
								handleChange={handleChange}
								increaseCostField={increaseCostField}
								inputData={inputData}
								isCostingManager={isCostingManager}
								isValidInput={isValidInput}
								prevTarget={prevTarget}
								resetAll={resetAll}
								setIncreaseCostField={setIncreaseCostField}
								updateCosting={updateCosting}
								showMore={showMore}
								readOnly={readOnly}
								setShowMore={setShowMore}
								validateFloatNumber={validateFloatNumber}
								selectedPriceRangeStatus={selectedPriceRangeStatus}
								handleDropdownChange={handleDropdownChange}
								COSTING_DROPDOWN_OPTIONS={COSTING_DROPDOWN_OPTIONS}
							/>
						)}
						<div className="predict-cost-button">
							{inputData?.isAiGeneratedCost && (
								<button
									className="button outline size32"
									onClick={() => handlePredictCost('OCS')}
								>
									<span className="mr-1">OCS</span>
								</button>
							)}
							{!inputData?.isAiGeneratedCost && aiGeneratedCostRoles() && (
								<button
									className="button size32 ml-2"
									onClick={() => handlePredictCost('AI')}
								>
									<span className="mr-1">AI Cost</span>
								</button>
							)}
						</div>

						<div className="unit-cost-details d-flex align-items-center mb-3">
							<div className="left-half d-flex justify-content-between align-items-center">
								<span className="regular-14 gray_dark_01">
									{isCostingForSetItem ? 'SET COST' : 'UNIT COST'}
								</span>
								{!isShowFactoryCosting &&
									(isCostingForSetItem
										? renderTotalCost(costingDetailsForSet?.totalCost)
										: renderTotalCost(inputData?.totalCost))}
							</div>
							<div className="right-half">
								{isShowFactoryCosting ? (
									<button className="button text regular-14 d-flex">Price/Size/Qty</button>
								) : (
									renderSizeQtyBtn()
								)}
								{!isShowFactoryCosting && renderBaseSizeAndCostingModal()}
							</div>
						</div>
					</div>
					<div className="attach-file px-4 pb-4">
						<p className="regular-14  gray_dark_02 pb-1">Attachment</p>
						<div className="d-flex justify-content-start align-items-center">
							<div
								className={`inline-upload mr-1  ${!inputData.id ? 'cursor-none' : 'cursor-pointer'}`}
							>
								<label htmlFor="psdUpload" className="regular-14  gray_dark_01">
									<img src={`${process.env.PUBLIC_URL}/icons/upload-dark.svg`} /> Upload
								</label>
								<input
									type="file"
									className="file-upload d-none"
									id="psdUpload"
									name="image"
									disabled={!inputData.id}
									onChange={(e) =>
										onMultipleFileUpload(e, 'PRODUCT_DESIGN', onUploadDocument)
									}
									multiple
								/>
							</div>
							<div className="pdf-files d-flex flex-wrap gap-2">{renderDocumentList()}</div>
						</div>
					</div>
					<div className="factory-value-and-file">
						<div className="costing-input custom-chekbox">
							<div className="form-group m-0">
								<input
									type="checkbox"
									id="person2"
									name="factoryCosting"
									checked={isShowFactoryCosting}
									onChange={async () => {
										// await toggleFactoryCosting(!isShowFactoryCosting);
										setIsShowFactoryCosting((prev) => !prev);
										// await fetchInitialCosting();
									}}
								/>
								<label className="regular-14" htmlFor="person2">
									<span>Costing from Factory</span>
								</label>
							</div>
						</div>
						<div
							className="factory-name-value d-flex justify-content-start"
							disabled={!isShowFactoryCosting}
						>
							<div className="factory-name button text d-flex justify-content-between align-items-center">
								<div className="form-group">
									<CustomDropdown
										toolTipLength={20}
										type="addItem"
										title={'Select'}
										isShowLabel={true}
										onItemClick={(obj) => {
											setSelectedSupplier(obj);
											updateCosting({}, false, false, obj.id);
										}}
										items={suppliers}
										onItemSearch={(value) => getSupplierList(`?name=${value}`)}
										selectedItem={selectedSupplier?.value || ''}
										dropdownIconPath={`${process.env.PUBLIC_URL}/icons/arrow_drop_up.svg`}
									/>
								</div>
							</div>
							<div className="factory-value button text">
								{!isShowFactoryCosting ? (
									<button className="button text regular-14 d-flex">Price/Size/Qty </button>
								) : (
									renderSizeQtyBtn()
								)}
								{isShowFactoryCosting && renderBaseSizeAndCostingModal()}
							</div>
						</div>
					</div>
					<div className="main-fabric-details">
						<div className="form-group">
							<label htmlFor="style-title">Lead Time</label>
							<input
								type="text"
								autoComplete="off"
								name="style-title"
								id="style-title"
								value={leadTime}
								onChange={handleLeadTime}
								onBlur={saveLeadTime}
							/>
						</div>
					</div>

					<div className="main-fabric-details">
						<p className="regular-14 gray_dark_01">
							{isCostingRequestFromBuyer ? inputData?.variation : inputData?.fabricName}
						</p>
					</div>

					<div className="qty-unit-dtails-table">
						<table className="table">
							<thead>
								<tr>
									<th>QTY</th>
									<th>Unit cost</th>
								</tr>
							</thead>
							<tbody>
								{!isCostingRequestFromBuyer && (
									<tr>
										<td>
											<span className="gray_dark_01">{inputData?.moq}</span>
										</td>
										<td>
											<span className="gray_dark_01">
												{isNaN(Number(inputData?.totalCost).toFixed(2))
													? ''
													: Number(inputData?.totalCost).toFixed(2)}
											</span>
										</td>
									</tr>
								)}
								{renderCostList()}
							</tbody>
						</table>
					</div>

					<div className="costing-remarks" key={`remarks_${costingId}`}>
						<textarea
							placeholder="Remarks"
							name="remarks"
							value={inputData?.remarks || ''}
							onChange={handleRemarks}
							onFocus={(event) => (prevTarget.current = event.target.value)}
							onBlur={(event) => {
								if (!isValidInput(prevTarget, event)) {
									resetAll();
									return;
								}
								saveRemarks();
							}}
							disabled={readOnly}
						/>
					</div>
					<p className="regulr-12 gray_dark_02 last-modified">
						<span className="d-block">Created by: {inputData?.priceLastModifiedBy}</span>
						{changeDateFormat(inputData?.priceLastModifiedAt, 'YYYY-MM-DD', 'DD MMM, YYYY') ===
						'Invalid date'
							? ''
							: 'Last modified: ' +
								convertDateToLocal(inputData?.priceLastModifiedAt, 'Do MMM YY, h:m a')}
					</p>
				</div>
			</div>
		</div>
	);
};

export default CostingForm;
